<template>
  <v-container class="page__wrapper">
    <v-row>
      <v-col cols="6">{{ $t('user') }}</v-col>
      <v-col cols="6" class="text-right">
        <v-dialog v-model="dialog" persistent max-width="600px">
          <template #activator="{ on, attrs }">
            <v-btn
              color="primary btn-custom"
              v-bind="attrs"
              v-on="on"
              :disabled="
                !getPermission('setting_setting') ||
                members.length >= maxAdminMember
              "
            >
              {{ $t('add_user') }}
            </v-btn>
          </template>
          <v-card>
            <div
              class="pointer"
              style="float: right; margin-right: 10px; margin-top: 10px"
            >
              <v-icon @click="dialog = false">mdi-close</v-icon>
            </div>
            <div class="pa-3">
              <validation-observer ref="addMemberForm" v-slot="{ invalid }">
                <form @submit.prevent="handleAddMember">
                  <v-card-title style="border-bottom: 0px solid">
                    <h3>{{ $t('Add') }} {{ $t('Account') }}</h3>
                  </v-card-title>
                  <v-card-text>
                    <div>
                      <span style="font-size: small">{{
                        $t('people_with_access')
                      }}</span>
                    </div>
                    <div class="d-flex" style="align-items: center">
                      <div>
                        <span class="mt-3">
                          {{ $t('Email') }}
                          <span class="color-red">*</span></span
                        >
                        <validation-provider
                          v-slot="{ errors }"
                          :name="$t('Email')"
                          rules="required|email"
                        >
                          <v-text-field
                            v-model="addMember.email"
                            dense
                            outlined
                            :error-messages="errors"
                          ></v-text-field>
                        </validation-provider>
                      </div>
                      <div class="mx-3">
                        <span class="mt-3">
                          {{ $t('role') }}
                        </span>
                        <v-select
                          :items="roles"
                          item-text="label"
                          item-value="value"
                          v-model="addMember.channel_member_role.id"
                          dense
                          outlined
                          style="width: 100%"
                        />
                      </div>
                      <v-btn
                        icon
                        :disabled="invalid"
                        type="submit"
                        color="primary"
                      >
                        <v-icon>mdi-send</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>
                </form>
              </validation-observer>
            </div>
          </v-card>
        </v-dialog>
      </v-col>

      <v-col style="overflow-x: auto">
        <v-data-table
          :headers="headers"
          :items="members"
          item-value="id"
          :items-per-page="6"
          :footer-props="{
            itemsPerPageOptions: [6],
            itemsPerPageText: ''
          }"
          class="elevation-1 mobile-data-table"
          mobile-breakpoint="0"
        >
          <template v-slot:item.role="{ item }">
            <v-select
              :items="roles"
              item-text="label"
              item-value="value"
              v-model="item.role"
              dense
              hide-details
              flat
              solo
              style="max-width: 120px"
              :disabled="
                item.status !== 'VERIFIED' || !getPermission('setting_setting')
              "
              @change="handleUpdateMemberRole(item)"
            />
          </template>
          <template v-slot:item.status="{ item }">
            <span
              :style="{
                color: item.status === 'VERIFIED' ? 'green' : 'red'
              }"
            >
              {{ item.status }}
            </span>
          </template>
          <template v-slot:item.id="{ item }">
            <v-btn
              v-if="item.email !== account.email"
              color="grey"
              fab
              x-small
              outlined
              dark
              @click="handleDeleteMember(item.id)"
              :disabled="!getPermission('setting_setting')"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <template>
      <div class="text-center mt-8">
        <v-divider></v-divider>

        <div class="expand-icon-container" @click="isExpanded = !isExpanded">
          <v-icon class="expand-icon" color="primary" large>
            {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </div>

        <v-expand-transition>
          <div v-show="isExpanded" class="mt-4">
            <div class="d-flex align-center justify-space-between">
              <span> {{ $t('delete_business') }} </span>
              <v-btn
                color="btn-custom"
                outlined
                @click="deleteBusiness"
                :disabled="!getPermission('setting_setting')"
              >
                <v-icon left>mdi-delete</v-icon>
                {{ $t('delete_business') }}
              </v-btn>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </template>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import useBusinessProfileService from '@/services/useBusinessProfileService'

export default {
  data() {
    const locale = this.currentLang
    return {
      dialog: false,
      isExpanded: false,
      roles: [],
      members: [],
      addMember: {
        email: null,
        channel_member_role: { id: '0' },
        email_lang: locale
      },
      maxAdminMember: 5
    }
  },
  computed: {
    ...mapGetters({
      account: 'ssoAccount/getAccountStore',
      permissionRole: 'ssoAccount/getPermissionRoleStore'
    }),
    headers() {
      return [
        {
          value: 'email',
          text: this.$t('email'),
          align: 'center'
        },
        {
          value: 'full_name',
          text: this.$t('fullname'),
          align: 'center'
        },
        {
          value: 'role',
          text: this.$t('people_with_access'),
          align: 'center'
        },
        {
          value: 'status',
          text: this.$t('status'),
          align: 'center'
        },
        {
          value: 'id',
          text: '',
          align: 'center'
        }
      ]
    }
  },
  async mounted() {
    await this.getMemberRole()
    await this.getProfileMembers()
    await this.getMaxAdminMember()
  },
  methods: {
    async getMemberRole() {
      this.$store.dispatch('setOverlay', true)
      var res = await this.$store.dispatch('ssoAccount/getMemberRole')
      if (res) {
        this.$store.dispatch('setOverlay', false)
        this.roles = res.data
      } else {
        this.$store.dispatch('setOverlay', false)
        this.$notify({
          group: 'foo',
          type: 'error',
          title: 'Error',
          text: `Something went wrong.`
        })
      }
    },
    async getProfileMembers() {
      this.$store.dispatch('setOverlay', true)
      var res = await this.$store.dispatch('ssoAccount/getProfileMembers', {
        page_number: 0,
        page_size: 5
      })
      if (res) {
        this.$store.dispatch('setOverlay', false)
        const formattedMembers = res.data.result.channel_member.map(
          (member) => ({
            ...member,
            email: member.member_login_data.email,
            full_name:
              (member.member_account.first_name || '') +
              ' ' +
              (member.member_account.last_name || ''),
            status: member.email_validation_status.status_description,
            role: member.channel_member_role.id.toString()
          })
        )
        this.members = formattedMembers
      } else {
        this.$store.dispatch('setOverlay', false)
        this.$notify({
          group: 'foo',
          type: 'error',
          title: 'Error',
          text: `Something went wrong.`
        })
      }
    },
    async handleAddMember() {
      this.$store.dispatch('setOverlay', true)
      var res = await this.$store.dispatch(
        'ssoAccount/postProfileMember',
        this.addMember
      )
      if (res && res.data.success) {
        await this.getProfileMembers()
        this.$nextTick(() => this.$refs.addMemberForm.reset())
        this.dialog = false
      } else {
        this.$store.dispatch('setOverlay', false)
        this.$notify({
          group: 'foo',
          type: 'error',
          title: 'Error',
          text: res.data.message || `Something went wrong.`
        })
      }
      this.$store.dispatch('setOverlay', false)
    },
    async handleUpdateMemberRole(member) {
      try {
        let countAdmin = 0
        this.members.forEach((element) => {
          if (
            element.channel_member_role.id === 1 ||
            element.channel_member_role.id === '1'
          ) {
            countAdmin++
          }
        })

        if (
          countAdmin === 1 &&
          (member.channel_member_role.id === 1 ||
            member.channel_member_role.id === '1')
        ) {
          this.$fire({
            title: 'ต้องมี Admin อย่างน้อย 1 คน',
            type: 'error',
            showCancelButton: false
          })

          await this.getProfileMembers()
          return false
        }

        this.$store.dispatch('setOverlay', true)
        var res = await this.$store.dispatch('ssoAccount/patchProfileMember', {
          id: member.id,
          email: member.email,
          channel_member_role: { id: member.role }
        })
        if (res && res.response.data.success) {
          await this.getProfileMembers()
        } else {
          this.$notify({
            group: 'foo',
            type: 'error',
            title: 'Error',
            text: res.response.data.message || `Something went wrong.`
          })
        }
      } finally {
        this.$store.dispatch('setOverlay', false)
      }
    },
    async handleDeleteMember(id) {
      this.$confirm(
        this.$t('confirm_delete_account'),
        this.$t('warning_access_business'),
        'question',
        {
          reverseButtons: true,
          confirmButtonText: this.$t('confirm'),
          cancelButtonText: this.$t('cancel')
        }
      )
        .then(async () => {
          this.$store.dispatch('setOverlay', true)
          var res = await this.$store.dispatch(
            'ssoAccount/deleteProfileMember',
            { id }
          )
          if (res && res.data.success) {
            await this.getProfileMembers()
          } else {
            this.$store.dispatch('setOverlay', false)
            this.$notify({
              group: 'foo',
              type: 'error',
              title: 'Error',
              text: res.data.message || `Something went wrong.`
            })
          }
          this.$store.dispatch('setOverlay', false)
        })
        .catch(() => {
          // console.log("cancel");
        })
    },
    async deleteBusiness() {
      this.$confirm(this.$t('confirm_delete_business'), '', 'question', {
        reverseButtons: true,
        confirmButtonText: this.$t('confirm'),
        cancelButtonText: this.$t('cancel')
      })
        .then(async () => {
          try {
            this.$store.dispatch('setOverlay', true)
            var res = await this.$store.dispatch('ssoAccount/deleteBusiness')
            if (res && res.data.success) {
              const path = `/${this.currentLang}`
              if (this.$route.path !== path) {
                this.$router.push({ path })
              }
            } else {
              this.$notify({
                group: 'foo',
                type: 'error',
                title: 'Error',
                text: res.data.message || `Something went wrong.`
              })
            }
          } finally {
            this.$store.dispatch('setOverlay', false)
          }
        })
        .catch(() => {
          // console.log("cancel");
        })
        .finally(() => {
          this.$store.dispatch('setOverlay', false)
        })
    },

    getPermission(permissionName) {
      if (!this.permissionRole) return false
      const isAllow = this.permissionRole.permission[permissionName].rule_write
      return isAllow
    },

    async getMaxAdminMember() {
      this.$store.dispatch('setOverlay', true)
      try {
        const response =
          await useBusinessProfileService().handleGetBusinessProfile()
        this.maxAdminMember = response.number_of_members_available
      } catch (err) {
        this.$fire({
          title: this.$t(err.response.data?.message),
          type: 'error',
          reverseButtons: true,
          showCancelButton: false
        })
      } finally {
        this.$store.dispatch('setOverlay', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.expand-icon-container {
  position: relative;
  margin-top: -20px;
  background: var(--v-surface-base);
  width: fit-content;
  margin-left: auto;
  margin-right: auto;
  cursor: pointer;
}
.expand-icon {
  border-radius: 50%;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
}
.mobile-data-table {
  min-width: 600px;
}
</style>
